import React, { FC, useCallback, useState } from 'react'
import {
  FormikConfig,
  FormikContext,
  FormikHelpers,
  FormikValues,
  useFormik,
} from 'formik'

import { ErrorBoundary } from '../ErrorBoundary'
import { Stack } from '../Stack'
import { LinkText, Text } from '../Text'
import { ExternalLink } from '../Link'

interface FormProps {
  initialValues: FormikValues
}

const FormError: React.FC = () => {
  return (
    <Stack>
      <Text variant='errorBox'>
        Something went wrong, please email{' '}
        <ExternalLink to='mailto:hi@guild.host'>
          <LinkText>hi@guild.host</LinkText>
        </ExternalLink>{' '}
        for help.
      </Text>
    </Stack>
  )
}

export const Form = ({
  children,
  initialValues,
  validationSchema,
  onSubmit,
  ...formikProps
}: FormProps & FormikConfig<any> & { children: React.ReactNode }) => {
  const [isSubmissionError, setIsSubmissionError] = useState(false)
  const formOnSubmit = useCallback(
    async (values, formikHelpers: FormikHelpers<any>) => {
      try {
        await onSubmit(values, formikHelpers)
        setIsSubmissionError(false)
      } catch (error) {
        setIsSubmissionError(true)
      }
    },
    [onSubmit]
  )

  const formik = useFormik({
    ...formikProps,
    initialValues,
    validationSchema,
    onSubmit: formOnSubmit,
  })

  return (
    <ErrorBoundary fallback={<FormError />}>
      <FormikContext.Provider value={formik}>
        {isSubmissionError && <FormError />}
        {children}
      </FormikContext.Provider>
    </ErrorBoundary>
  )
}
